import { useEffect, useState } from 'react';
import type { ActiveSession } from './auth';
// Deliberately tab-memory only. Credentials, online actions and file bytes have no field here.
const allowed = [
  'label',
  'summary',
  'category',
  'market',
  'quantity',
  'fillSize',
  'source',
  'revisionReason',
  'name',
  'role',
  'projectId',
  'expiresAt',
  'requestKey',
  'customerName',
  'brand',
  'brief',
  'skuLabels',
  'customerId',
  'brandId',
] as const;
type Draft = Record<string, string>;
let identity = '';
let sealed = false;
let expires = 0;
const drafts = new Map<string, Draft>();
export function clearResume() {
  identity = '';
  sealed = false;
  expires = 0;
  drafts.clear();
}
export function resumeIdentity(session: ActiveSession | null) {
  if (!session || session.offline) return;
  const scope = JSON.stringify([
    session.userId,
    session.organizationContexts,
    session.customerContexts,
    [...session.contexts]
      .sort((a, b) => a.projectId.localeCompare(b.projectId))
      .map((c) => [
        c.organizationId,
        c.projectId,
        c.audience,
        c.role,
        [...c.actions].sort(),
        [...c.readBundles].sort(),
      ]),
  ]);
  if ((identity && scope !== identity) || (expires && Date.now() >= expires)) clearResume();
  identity = scope;
  sealed = false;
}
export function sealResume() {
  if (identity) {
    sealed = true;
    expires = Date.now() + 600_000;
  }
}
export function captureResume(form: string, value: Record<string, unknown>) {
  if (!identity || sealed) return;
  const safe: Draft = {};
  for (const key of allowed)
    if (typeof value[key] === 'string') safe[key] = value[key].slice(0, 4000);
  if (JSON.stringify(safe).length > 20_000) return;
  if (!drafts.has(form) && drafts.size >= 8) drafts.delete(drafts.keys().next().value ?? '');
  drafts.set(form, safe);
}
export function readResume(form: string): Draft | null {
  if (expires && Date.now() >= expires) {
    clearResume();
    return null;
  }
  return !sealed && identity ? structuredClone(drafts.get(form) ?? null) : null;
}
export function useResumableFields(form: string, initial: Record<string, unknown> = {}) {
  const [value, setValue] = useState<Record<string, unknown>>(() => readResume(form) ?? initial);
  useEffect(() => {
    captureResume(form, value);
  }, [form, value]);
  return [value, setValue] as const;
}

export function useResumableString(
  form: string,
  field: string,
  initial: string | (() => string) = '',
) {
  const [state, setState] = useState(() => ({
    form,
    value: readResume(form)?.[field] ?? (typeof initial === 'function' ? initial() : initial),
  }));
  const value =
    state.form === form
      ? state.value
      : (readResume(form)?.[field] ?? (typeof initial === 'function' ? initial() : initial));
  if (state.form !== form) setState({ form, value });
  useEffect(() => {
    captureResume(form, { ...readResume(form), [field]: value });
  }, [form, field, value]);
  const update = (next: string) => setState({ form, value: next });
  return [value, update] as const;
}
